export const loadRecaptcha = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      resolve(); // No recaptcha configured
      return;
    }

    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    // Check if already loaded
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(() => resolve());
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve());
      } else {
        resolve();
      }
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });
};

/** Remove reCAPTCHA script and badge when leaving login/register/forgot-password so the badge does not show on other pages (e.g. account). */
export const unloadRecaptcha = (): void => {
  if (typeof document === 'undefined') return;
  const script = document.querySelector('script[data-hotlistcasino-recaptcha="1"]');
  if (script?.parentNode) {
    script.parentNode.removeChild(script);
  }
  const badge = document.querySelector('.grecaptcha-badge');
  if (badge?.parentNode) {
    badge.parentNode.removeChild(badge);
  }
  delete (window as Window & { grecaptcha?: unknown }).grecaptcha;
};

export const executeRecaptcha = async (action: string): Promise<string | undefined> => {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return undefined; // No recaptcha configured
  }

  if (typeof window === 'undefined' || !window.grecaptcha?.execute) {
    return undefined;
  }

  try {
    const token = await window.grecaptcha.execute(siteKey, { action });
    return token;
  } catch (error) {
    console.error('reCAPTCHA execution failed:', error);
    return undefined;
  }
};

// TypeScript declarations
declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}
